import { useEffect, useState } from "react";
import { BellRing, MessageCircle, PackageX } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { currentPushSubscription, ensurePushSubscription, pushSupported } from "@/lib/pushClient";

const Toggle = ({ on, onChange, disabled, testid }) => (
  <button type="button" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)} data-testid={testid}
    className={`relative w-11 h-6 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-stone-300"} disabled:opacity-50`}>
    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

/** Manager: per-device push toggles for low-stock and new-chat alerts. */
export function ManagerAlertsCard({ token }) {
  const [alerts, setAlerts] = useState(null); // null = not subscribed on this device
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const supported = pushSupported();

  useEffect(() => {
    if (!supported) { setLoading(false); return; }
    (async () => {
      try {
        const sub = await currentPushSubscription();
        const { data } = await adminApi(token).get("/manager/push/status", { params: { endpoint: sub?.endpoint || "" } });
        setAlerts(data.this_device);
      } catch { /* not subscribed */ }
      finally { setLoading(false); }
    })();
  }, [token, supported]);

  const save = async (next) => {
    setBusy(true);
    try {
      const sub = await ensurePushSubscription();
      if (!next.low_stock && !next.chat) {
        await adminApi(token).delete("/manager/push/subscribe", { params: { endpoint: sub.endpoint } });
        setAlerts(null);
        toast.success("Alerts off on this phone");
      } else {
        const { data } = await adminApi(token).post("/manager/push/subscribe", { subscription: sub.toJSON(), alerts: next });
        setAlerts(data.alerts);
        toast.success("Alerts updated for this phone");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Couldn't update alerts");
    } finally { setBusy(false); }
  };

  const cur = alerts || { low_stock: false, chat: false };
  const rows = [
    { key: "low_stock", label: "Low stock", hint: "When an ingredient drops below its threshold", Icon: PackageX },
    { key: "chat", label: "New customer chat", hint: "When a customer sends a message", Icon: MessageCircle },
  ];

  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="manager-alerts-card">
      <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-2 font-black text-stone-800 text-sm uppercase tracking-widest">
        <BellRing className="w-4 h-4 text-brand-red" /> Push alerts on this phone
        {alerts && <span className="ml-auto text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full" data-testid="manager-alerts-status">On</span>}
      </div>
      {!supported ? (
        <div className="px-5 py-4 text-sm text-stone-500" data-testid="manager-alerts-unsupported">This browser can't receive push notifications. On iPhone, install the app to your Home Screen first, then come back here.</div>
      ) : (
        <ul className="divide-y divide-stone-100">
          {rows.map(({ key, label, hint, Icon }) => (
            <li key={key} className="px-5 py-3 flex items-center gap-3" data-testid={`manager-alert-row-${key}`}>
              <Icon className="w-4 h-4 text-stone-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-stone-800 text-sm">{label}</div>
                <div className="text-[11px] text-stone-500">{hint}</div>
              </div>
              <Toggle on={!!cur[key]} disabled={busy || loading} onChange={(v) => save({ ...cur, [key]: v })} testid={`manager-alert-toggle-${key}`} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
